import logger from '@/lib/logger';
import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import ActiveBuddyCard from '@/components/buddy/ActiveBuddyCard';
import BuddyRequestSection from '@/components/buddy/BuddyRequestSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BuddyPage() {
  const [buddyData, setBuddyData] = useState(null);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, rRes] = await Promise.all([
        axios.get(`${API}/buddy/my-buddy`),
        axios.get(`${API}/buddy/requests`),
      ]);
      setBuddyData(bRes.data);
      setRequests(rRes.data);
    } catch (err) {
      logger.error('Buddy fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const sendRequest = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    setSuccess('');
    try {
      await axios.post(`${API}/buddy/request`, { target_email: email });
      setSuccess('Buddy request sent!');
      setEmail('');
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send request');
    } finally {
      setSending(false);
    }
  };

  const acceptRequest = async (id) => {
    try {
      await axios.post(`${API}/buddy/accept/${id}`);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to accept');
    }
  };

  const declineRequest = async (id) => {
    try {
      await axios.post(`${API}/buddy/decline/${id}`);
      fetchAll();
    } catch (err) {
      logger.error('Decline error:', err);
    }
  };

  const removeBuddy = async () => {
    if (!window.confirm('Remove your accountability buddy?')) return;
    try {
      await axios.delete(`${API}/buddy/remove`);
      fetchAll();
    } catch (err) {
      logger.error('Remove buddy error:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="buddy-page">
      <div className="mb-8">
        <div className="label-tactical text-[#FF3B30] mb-1">Accountability</div>
        <h1 className="text-4xl font-black tracking-tighter">BUDDY SYSTEM</h1>
        <p className="text-[#A1A1AA] mt-2">Partner up with a friend to stay accountable and motivated.</p>
      </div>

      {buddyData?.has_buddy ? (
        <ActiveBuddyCard buddyData={buddyData} onRemove={removeBuddy} />
      ) : (
        <BuddyRequestSection
          email={email}
          setEmail={setEmail}
          onSend={sendRequest}
          sending={sending}
          error={error}
          success={success}
          requests={requests}
          onAccept={acceptRequest}
          onDecline={declineRequest}
        />
      )}
    </div>
  );
}
